// RPSignIn.js — Responsible Pharmacist sign-in / session payment module
//
// Opened from the RP sign-in card on HeadOffice.js.
//
// Default view: one row per pharmacy showing today's RP state —
//   green  = a pharmacist is signed in right now (open session today)
//   amber  = signed in today and already signed out
//   red    = no RP sign-in recorded today at all
//
// Clicking a pharmacy opens a tabbed panel beneath the grid:
//   1. Pharmacy view    month grid for one branch, editable notes + date paid,
//                       an optional single-pharmacist filter, hours/pay totals
//                       and a bulk "Mark all as paid"
//   2. Unpaid sessions  fleet-wide sweep of sessions with no date paid
//
// The old "Pharmacist view" tab was removed: filtering the month grid to one
// pharmacist does the same job in the place people were already looking, and
// two routes to the same figures invited disagreement between them.
//
// TIMES (v2)
// ----------
// rp_signin_logs stores true UTC instants — a 17:06 BST sign-in is 16:06Z.
// rp_signin_routes.js now converts on the way out, so signInTime/signOutTime
// arrive as UK WALL CLOCK carried in the UTC fields of an ISO string. Every
// render here therefore uses timeZone:'UTC' / getUTC*(), which gives the UK
// clock whatever time zone the browser is in. Do NOT "helpfully" switch these
// back to Europe/London: that would add the BST hour a second time.
//
// PAY RATES
// ---------
// Hours come from the RP register; the rate does not. Rates live in the
// head-office pharmacist register (rota_pharmacists) and are joined on the
// GPhC number, the only field the two sides share. No GPhC, or no rate set,
// means the money column stays blank rather than guessing.
//
// DATA MODEL NOTE
// ---------------
// rp_signin_logs (per tenant) is the statutory RP register and is NOT written
// to by this module. Payment/admin data lives in a separate per-tenant
// collection, rp_signin_payments, keyed by the session _id:
//
//   { _id, sessionId, datePaid, notes, updatedBy, updatedAt, createdAt,
//     isDeleted }
//   unique index { sessionId: 1 }
//   index        { isDeleted: 1, datePaid: 1 }
//
// See the endpoint contract at the bottom of this file.

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useMaskNames, displayName } from "./nameMask";
// Shared pharmacist register dialogue, owned by the rota module. Opened here
// from the toolbar with showDays={false}: the permanent weekly pattern is a
// rota concern, and the register matters on this screen because the hourly
// pay rate behind the money column lives in it.
import { PharmacistListPopup } from "./PharmacistRota";

const API_BASE = "https://api.pharmacegroup.co.uk";

// Set false once the backend endpoints are live. While true, any failed
// fetch falls back to sample data so the layout can be reviewed.
const DEMO_FALLBACK = true;

// ---------------------------------------------------------------------------
// API helpers (mirrors HeadOffice.js conventions)
// ---------------------------------------------------------------------------
function withOrgScope(path) {
  const org = localStorage.getItem("organisationName") || "";
  if (!org) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}organisationName=${encodeURIComponent(org)}`;
}

async function apiGet(path, token) {
  const res = await fetch(`${API_BASE}/api/headoffice/${withOrgScope(path)}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!res.ok) throw new Error(`${path} → HTTP ${res.status}`);
  return res.json();
}

async function apiSend(path, token, method = "POST", body = null) {
  const res = await fetch(`${API_BASE}/api/headoffice/${withOrgScope(path)}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: body != null ? JSON.stringify(body) : undefined
  });
  if (!res.ok) throw new Error(`${path} → HTTP ${res.status}`);
  return res.json();
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const GREEN = "#dcfce7", AMBER = "#fef3c7", RED = "#fde2e1";
const GREEN_BD = "#86efac", AMBER_BD = "#fcd34d", RED_BD = "#fca5a5";

const S = {
  wrap:      { padding: 4, fontFamily: "system-ui, sans-serif" },
  h1:        { fontSize: 18, fontWeight: 600, margin: "0 0 4px" },
  sub:       { fontSize: 13, color: "#6b7280", margin: "0 0 12px" },
  panel:     { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6,
               padding: 14, marginTop: 12 },
  table:     { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th:        { textAlign: "left", borderBottom: "1px solid #d1d5db",
               padding: "6px 8px", fontWeight: 600, color: "#374151",
               whiteSpace: "nowrap", background: "#f9fafb",
               // Sticks against the page now that the grid has no inner
               // scroll box, so it needs an opaque background and enough
               // z-index to sit over the rows passing beneath it.
               position: "sticky", top: 0, zIndex: 2,
               boxShadow: "inset 0 -1px 0 #d1d5db" },
  td:        { borderBottom: "1px solid #f3f4f6", padding: "5px 8px",
               color: "#1f2937", verticalAlign: "middle" },
  btn:       { background: "#2563eb", color: "#fff", border: "none",
               borderRadius: 4, padding: "6px 12px", cursor: "pointer",
               fontSize: 13 },
  btnGhost:  { background: "#f3f4f6", color: "#374151",
               border: "1px solid #d1d5db", borderRadius: 4,
               padding: "6px 12px", cursor: "pointer", fontSize: 13 },
  btnTiny:   { background: "#eff6ff", color: "#1d4ed8",
               border: "1px solid #bfdbfe", borderRadius: 4,
               padding: "3px 8px", cursor: "pointer", fontSize: 12,
               whiteSpace: "nowrap" },
  select:    { border: "1px solid #d1d5db", borderRadius: 4, padding: "5px 8px",
               fontSize: 13, background: "#fff" },
  input:     { border: "1px solid #d1d5db", borderRadius: 4, padding: "3px 6px",
               fontSize: 12, width: "100%", boxSizing: "border-box" },
  tabBar:    { display: "flex", gap: 4, borderBottom: "1px solid #e5e7eb",
               marginBottom: 12 },
  tab:       (on) => ({
               padding: "8px 16px", fontSize: 13, cursor: "pointer",
               border: "1px solid", borderColor: on ? "#e5e7eb" : "transparent",
               borderBottom: on ? "1px solid #fff" : "1px solid #e5e7eb",
               borderRadius: "6px 6px 0 0", marginBottom: -1,
               background: on ? "#fff" : "transparent",
               fontWeight: on ? 600 : 400,
               color: on ? "#111827" : "#6b7280" }),
  ctrlRow:   { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
               marginBottom: 12 },
  monthNav:  { display: "flex", alignItems: "center", gap: 6 },
  arrow:     { background: "#f3f4f6", border: "1px solid #d1d5db",
               borderRadius: 4, width: 28, height: 28, cursor: "pointer",
               fontSize: 14, lineHeight: 1, color: "#374151" },
  monthLbl:  { fontSize: 13, fontWeight: 600, minWidth: 130,
               textAlign: "center" },
  error:     { color: "#b91c1c", fontSize: 13, padding: "4px 0" },
  muted:     { color: "#9ca3af" },
  banner:    { background: "#fffbeb", border: "1px solid #fcd34d",
               borderRadius: 4, padding: "6px 10px", fontSize: 12,
               color: "#92400e", marginBottom: 10 },
  legend:    { display: "flex", gap: 14, fontSize: 12, color: "#4b5563",
               marginBottom: 10, flexWrap: "wrap" },
  swatch:    (bg, bd) => ({ display: "inline-block", width: 12, height: 12,
               background: bg, border: `1px solid ${bd}`, borderRadius: 2,
               marginRight: 5, verticalAlign: -1 }),
  overlay:   { position: "fixed", inset: 0, background: "rgba(17,24,39,0.45)",
               display: "flex", alignItems: "center", justifyContent: "center",
               zIndex: 1200 },
  dialog:    { background: "#fff", borderRadius: 8, padding: 18, width: 520,
               maxWidth: "92vw", boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
               fontFamily: "system-ui, sans-serif" }
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const MONTHS = ["January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Session times arrive as UK wall clock carried in the UTC fields (see the
// note at the top of this file), so they are read back with UTC accessors.
const fmtTime = (d) => d
  ? new Date(d).toLocaleTimeString("en-GB",
      { hour: "2-digit", minute: "2-digit", timeZone: "UTC" })
  : "—";

const fmtSessionDate = (d) => d
  ? new Date(d).toLocaleDateString("en-GB", { timeZone: "UTC" }) : "";
const sessionDayOfMonth = (d) => new Date(d).getUTCDate();
const sessionDow = (d) => DOW[new Date(d).getUTCDay()];

// datePaid is an ordinary calendar date, not a session time — plain local.
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-GB") : "";

// Money. Blank (not zero) when there is no rate to apply.
const fmtMoney = (n) => (n == null || !Number.isFinite(n))
  ? "—" : `£${n.toFixed(2)}`;

// Normalised GPhC — the join key between an RP session and the pay register.
const gphcKey = (v) => String(v || "").trim().replace(/\s+/g, "");

// Decimal hours → "7h 30m"
const fmtHours = (h) => {
  if (h == null) return "—";
  const mins = Math.round(h * 60);
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, "0")}m`;
};

const hoursBetween = (a, b) =>
  (a && b) ? Math.round(((new Date(b) - new Date(a)) / 3600000) * 100) / 100 : null;

const isoToday = () => new Date().toISOString().slice(0, 10);
const toISODate = (d) => d ? new Date(d).toISOString().slice(0, 10) : "";
const daysInMonth = (y, m) => new Date(y, m, 0).getDate();   // m is 1-based

// ---------------------------------------------------------------------------
// Month stepper — arrows either side of the month, plus a year selector
// ---------------------------------------------------------------------------
function MonthNav({ year, month, onChange }) {
  const step = (delta) => {
    let y = year, m = month + delta;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    onChange(y, m);
  };
  const years = [];
  const thisYear = new Date().getFullYear();
  for (let y = thisYear - 3; y <= thisYear + 1; y++) years.push(y);

  return (
    <div style={S.monthNav}>
      <button style={S.arrow} onClick={() => step(-1)} title="Previous month">‹</button>
      <span style={S.monthLbl}>{MONTHS[month - 1]} {year}</span>
      <button style={S.arrow} onClick={() => step(1)} title="Next month">›</button>
      <select style={S.select} value={year}
              onChange={(e) => onChange(Number(e.target.value), month)}>
        {years.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DatePaidCell — button when unpaid, double-click to edit when paid
// ---------------------------------------------------------------------------
function DatePaidCell({ session, onSave, disabled }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(toISODate(session?.datePaid));

  useEffect(() => { setVal(toISODate(session?.datePaid)); }, [session?.datePaid]);

  if (!session?.sessionId) return <span style={S.muted}>—</span>;

  const commit = () => {
    setEditing(false);
    if (val !== toISODate(session.datePaid)) onSave(val || null);
  };

  if (editing) {
    return (
      <input type="date" autoFocus style={{ ...S.input, width: 130 }}
             value={val}
             onChange={(e) => setVal(e.target.value)}
             onBlur={commit}
             onKeyDown={(e) => {
               if (e.key === "Enter") commit();
               if (e.key === "Escape") {
                 setVal(toISODate(session.datePaid));
                 setEditing(false);
               }
             }} />
    );
  }

  if (!session.datePaid) {
    return (
      <button style={S.btnTiny} disabled={disabled}
              onClick={() => onSave(isoToday())}
              title="Record today as the date this session was paid">
        Click to mark paid
      </button>
    );
  }

  return (
    <span onDoubleClick={() => setEditing(true)}
          title="Double-click to edit"
          style={{ cursor: "text", borderBottom: "1px dotted #9ca3af" }}>
      {fmtDate(session.datePaid)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// NotesCell — free text, saved on blur
// ---------------------------------------------------------------------------
function NotesCell({ session, onSave }) {
  const [val, setVal] = useState(session?.notes || "");
  useEffect(() => { setVal(session?.notes || ""); }, [session?.notes]);

  if (!session?.sessionId) return <span style={S.muted}>—</span>;

  return (
    <input style={S.input} value={val} placeholder="—"
           onChange={(e) => setVal(e.target.value)}
           onBlur={() => { if (val !== (session.notes || "")) onSave(val); }}
           onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }} />
  );
}

// ===========================================================================
// TAB 1 — Pharmacy view
// ===========================================================================
function PharmacyView({ token, pharmacies, pharmacyId, onPharmacyChange,
                        year, month, onMonthChange, rates, onSaved }) {
  const masked = useMaskNames();
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [person, setPerson] = useState("");        // "" = every pharmacist
  const [confirmPay, setConfirmPay] = useState(null);  // { targets, label }
  const [paying, setPaying] = useState(false);

  const load = useCallback(async () => {
    if (!pharmacyId) return;
    setLoading(true); setError(null);
    try {
      const d = await apiGet(
        `rp-signin/month?pharmacyId=${pharmacyId}&year=${year}&month=${month}`,
        token);
      setRows(Array.isArray(d?.sessions) ? d.sessions : []);
    } catch (e) {
      if (DEMO_FALLBACK) setRows(demoMonth(year, month));
      else setError(e.message);
    } finally { setLoading(false); }
  }, [token, pharmacyId, year, month]);

  useEffect(() => { load(); }, [load]);

  const save = async (session, patch) => {
    // Optimistic — the payment doc is keyed on sessionId in rp_signin_payments.
    setRows(rs => rs.map(r =>
      r.sessionId === session.sessionId ? { ...r, ...patch } : r));
    try {
      await apiSend("rp-signin/payment", token, "POST",
        { pharmacyId, sessionId: session.sessionId, ...patch });
      onSaved && onSaved();
    } catch (e) {
      if (!DEMO_FALLBACK) { setError(e.message); load(); }
    }
  };

  /* Everyone who appears in this month at this branch, for the filter.
     Keyed on GPhC where there is one so the same person recorded under two
     spellings collapses to one entry; falls back to the name otherwise. */
  const people = useMemo(() => {
    const by = new Map();
    for (const s of (rows || [])) {
      const key = gphcKey(s.gphc) || `name:${(s.pharmacistName || "").toLowerCase()}`;
      if (!by.has(key)) {
        by.set(key, { key, name: s.pharmacistName || "Unknown",
                      gphc: gphcKey(s.gphc) || null, sessions: 0 });
      }
      by.get(key).sessions++;
    }
    return [...by.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const keyOf = (s) =>
    gphcKey(s.gphc) || `name:${(s.pharmacistName || "").toLowerCase()}`;

  // The sessions actually on screen — everything the totals and the bulk
  // payment act on. Filtering by pharmacist narrows both.
  const shown = useMemo(
    () => (rows || []).filter(s => !person || keyOf(s) === person),
    [rows, person]);

  const rateFor = (s) => {
    const g = gphcKey(s.gphc);
    if (!g || !rates) return null;
    const hit = rates[g];
    return hit && Number.isFinite(Number(hit.hourlyRate)) ? Number(hit.hourlyRate) : null;
  };
  const hoursFor = (s) => s.hours ?? hoursBetween(s.signInTime, s.signOutTime);
  const payFor = (s) => {
    const r = rateFor(s), h = hoursFor(s);
    return (r == null || h == null) ? null : Math.round(r * h * 100) / 100;
  };

  // Unfiltered, the grid is the whole month: the empty days are the point —
  // an uncovered Tuesday is what you are looking for. Filtered to ONE
  // pharmacist, empty days say nothing (they were not working, that is not
  // news) and 28 blank rows bury the three that matter, so those days are
  // dropped and only their sessions remain.
  // A day with two sessions gets a second row with the day cell left blank.
  const grid = useMemo(() => {
    const n = daysInMonth(year, month);
    const byDay = new Map();
    for (const s of shown) {
      const d = sessionDayOfMonth(s.signInTime);
      if (!byDay.has(d)) byDay.set(d, []);
      byDay.get(d).push(s);
    }
    const out = [];
    for (let day = 1; day <= n; day++) {
      const dow = DOW[new Date(year, month - 1, day).getDay()];
      const list = byDay.get(day) || [];
      if (!list.length) {
        if (person) continue;                      // filtered: skip blank days
        out.push({ day, dow, session: null, first: true });
      } else {
        list.forEach((s, i) =>
          out.push({ day, dow, session: s, first: i === 0 }));
      }
    }
    return out;
  }, [shown, year, month, person]);

  const totals = useMemo(() => {
    let hours = 0, pay = 0, unpaid = 0, noRate = 0, noHours = 0;
    for (const s of shown) {
      const h = hoursFor(s), amt = payFor(s);
      if (h == null) noHours++; else hours += h;
      if (amt == null) { if (h != null) noRate++; } else pay += amt;
      if (!s.datePaid) unpaid++;
    }
    return {
      sessions: shown.length,
      hours: Math.round(hours * 100) / 100,
      pay: Math.round(pay * 100) / 100,
      unpaid, noRate, noHours
    };
  }, [shown, rates]);   // eslint-disable-line react-hooks/exhaustive-deps

  /* ---- Mark all as paid -------------------------------------------------
     Acts on exactly what is on screen — the month, the branch, and the
     pharmacist filter if one is set. Already-paid sessions are left alone
     so the button can't quietly rewrite a date somebody entered by hand. */
  const askMarkAll = () => {
    const targets = shown.filter(s => s.sessionId && !s.datePaid);
    if (!targets.length) return;
    const who = person
      ? (people.find(p => p.key === person)?.name || "the selected pharmacist")
      : "every pharmacist";
    setConfirmPay({ targets, who });
  };

  const doMarkAll = async () => {
    if (!confirmPay || paying) return;
    setPaying(true);
    const ids = confirmPay.targets.map(s => s.sessionId);
    const today = isoToday();
    try {
      await apiSend("rp-signin/payment/bulk", token, "POST",
        { pharmacyId, sessionIds: ids, datePaid: today });
      const set = new Set(ids);
      setRows(rs => rs.map(r => set.has(r.sessionId) ? { ...r, datePaid: today } : r));
      onSaved && onSaved();
      setConfirmPay(null);
    } catch (e) {
      setError(`Bulk payment failed — nothing was changed. ${e.message}`);
      setConfirmPay(null);
      load();
    } finally { setPaying(false); }
  };

  const unpaidOnScreen = shown.filter(s => s.sessionId && !s.datePaid).length;

  return (
    <div>
      <div style={S.ctrlRow}>
        <select style={S.select} value={pharmacyId || ""}
                onChange={(e) => onPharmacyChange(e.target.value)}>
          <option value="">Select a pharmacy…</option>
          {pharmacies.map(p => (
            <option key={p.pharmacyId} value={p.pharmacyId}>
              {displayName(p.pharmacyName, masked)}
            </option>
          ))}
        </select>
        <MonthNav year={year} month={month} onChange={onMonthChange} />

        <select style={S.select} value={person}
                title="Limit the month to a single pharmacist. The totals and the bulk payment follow the filter."
                onChange={(e) => setPerson(e.target.value)}>
          <option value="">All pharmacists</option>
          {people.map(p => (
            <option key={p.key} value={p.key}>
              {displayName(p.name, masked)}
              {p.gphc ? ` — ${p.gphc}` : " — no GPhC"}
              {` (${p.sessions})`}
            </option>
          ))}
        </select>

        <button style={{ ...S.btn, opacity: unpaidOnScreen ? 1 : 0.45,
                         cursor: unpaidOnScreen ? "pointer" : "default" }}
                disabled={!unpaidOnScreen}
                onClick={askMarkAll}
                title={unpaidOnScreen
                  ? "Mark every unpaid session currently shown as paid today"
                  : "Nothing on screen is unpaid"}>
          Mark all as paid{unpaidOnScreen ? ` (${unpaidOnScreen})` : ""}
        </button>

        <span style={{ marginLeft: "auto", fontSize: 12, color: "#4b5563" }}>
          {totals.sessions} session{totals.sessions === 1 ? "" : "s"} ·{" "}
          {fmtHours(totals.hours)} on site ·{" "}
          <b style={{ color: totals.unpaid ? "#b91c1c" : "#059669" }}>
            {totals.unpaid} unpaid
          </b>
        </span>
      </div>

      {error && <div style={S.error}>{error}</div>}
      {loading && <div style={S.sub}>Loading…</div>}

      {person && totals.noRate > 0 && (
        <div style={S.banner}>
          {totals.noRate} session{totals.noRate === 1 ? " has" : "s have"} no pay
          rate — the pharmacist's GPhC number isn't in the head-office
          pharmacist register, or no hourly rate is set against it. Those
          sessions are counted in the hours but not in the money.
        </div>
      )}

      {!pharmacyId ? (
        <div style={S.sub}>Choose a pharmacy above, or click one in the grid.</div>
      ) : (
        /* No inner scroll box. A month is a fixed, knowable length and the
           whole of it should be on the page at once — a 520px window meant
           reconciling a month by dragging a scrollbar inside a panel inside
           a page, and the totals row sat below the fold of a container most
           people never realised was scrollable. The page scrolls instead;
           the header row sticks as you go, so the columns stay labelled. */
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 4,
                      overflowX: "auto" }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={{ ...S.th, width: 40 }}>Day</th>
                <th style={{ ...S.th, width: 46 }}></th>
                <th style={S.th}>Pharmacist</th>
                <th style={{ ...S.th, width: 80 }}>Clock in</th>
                <th style={{ ...S.th, width: 80 }}>Clock out</th>
                <th style={{ ...S.th, width: 90 }}>Hours on site</th>
                <th style={{ ...S.th, width: 70 }}>Rate</th>
                <th style={{ ...S.th, width: 80 }}>Payment</th>
                <th style={{ ...S.th, minWidth: 180 }}>Notes</th>
                <th style={{ ...S.th, width: 140 }}>Date paid</th>
              </tr>
            </thead>
            <tbody>
              {grid.map((g, i) => {
                const s = g.session;
                const hrs = s ? hoursFor(s) : null;
                const rate = s ? rateFor(s) : null;
                const amt = s ? payFor(s) : null;
                const weekend = g.dow === "Sat" || g.dow === "Sun";
                return (
                  <tr key={i} style={{
                        background: !s ? (weekend ? "#f9fafb" : "#fff")
                                       : (s.datePaid ? "#f0fdf4" : "#fff") }}>
                    <td style={{ ...S.td, fontWeight: g.first ? 600 : 400,
                                 color: weekend ? "#9ca3af" : "#1f2937" }}>
                      {g.first ? g.day : ""}
                    </td>
                    <td style={{ ...S.td, fontSize: 11,
                                 color: weekend ? "#9ca3af" : "#6b7280" }}>
                      {g.first ? g.dow : ""}
                    </td>
                    <td style={S.td}>
                      {s ? displayName(s.pharmacistName || "Unknown", masked)
                         : <span style={S.muted}>—</span>}
                    </td>
                    <td style={S.td}>{s ? fmtTime(s.signInTime) : <span style={S.muted}>—</span>}</td>
                    <td style={S.td}>
                      {s ? (s.signOutTime ? fmtTime(s.signOutTime)
                                          : <span style={{ color: "#b45309" }}>open</span>)
                         : <span style={S.muted}>—</span>}
                    </td>
                    <td style={S.td}>{s ? fmtHours(hrs) : <span style={S.muted}>—</span>}</td>
                    <td style={S.td}>
                      {!s ? <span style={S.muted}>—</span>
                          : rate == null
                            ? <span style={S.muted} title="No hourly rate for this GPhC number in the pharmacist register">—</span>
                            : fmtMoney(rate)}
                    </td>
                    <td style={S.td}>
                      {!s ? <span style={S.muted}>—</span> : fmtMoney(amt)}
                    </td>
                    <td style={S.td}>
                      <NotesCell session={s} onSave={(v) => save(s, { notes: v })} />
                    </td>
                    <td style={S.td}>
                      <DatePaidCell session={s}
                                    onSave={(v) => save(s, { datePaid: v })} />
                    </td>
                  </tr>
                );
              })}
              {!grid.length && (
                <tr>
                  <td style={{ ...S.td, color: "#6b7280" }} colSpan={10}>
                    No sessions for this pharmacist at this branch in{" "}
                    {MONTHS[month - 1]} {year}.
                  </td>
                </tr>
              )}
            </tbody>

            {/* Totals for whatever is on screen — the whole branch month, or
                one pharmacist within it once the filter is set. */}
            <tfoot>
              <tr style={{ background: "#f9fafb", borderTop: "2px solid #d1d5db",
                           position: "relative", zIndex: 1 }}>
                <td style={{ ...S.td, fontWeight: 600 }} colSpan={5}>
                  {person
                    ? `Total — ${displayName(people.find(p => p.key === person)?.name || "", masked)}, ${MONTHS[month - 1]} ${year}`
                    : `Total — all pharmacists, ${MONTHS[month - 1]} ${year}`}
                  <span style={{ fontWeight: 400, color: "#6b7280", marginLeft: 8 }}>
                    {totals.sessions} session{totals.sessions === 1 ? "" : "s"}
                  </span>
                </td>
                <td style={{ ...S.td, fontWeight: 700 }}>{fmtHours(totals.hours)}</td>
                <td style={S.td}></td>
                <td style={{ ...S.td, fontWeight: 700 }}>{fmtMoney(totals.pay)}</td>
                <td style={{ ...S.td, fontSize: 11, color: "#6b7280" }} colSpan={2}>
                  {totals.noHours > 0 &&
                    `${totals.noHours} session${totals.noHours === 1 ? "" : "s"} still open · `}
                  {totals.noRate > 0
                    ? `${totals.noRate} with no rate — excluded from the money`
                    : "all sessions rated"}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* ---- Bulk payment confirmation ---------------------------------- */}
      {confirmPay && (
        <div style={S.overlay} onClick={() => !paying && setConfirmPay(null)}>
          <div style={S.dialog} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>
              Mark {confirmPay.targets.length} session
              {confirmPay.targets.length === 1 ? "" : "s"} as paid?
            </div>
            <div style={{ ...S.banner, marginBottom: 12 }}>
              This writes today's date against every unpaid session currently
              shown — <b>{displayName(confirmPay.who, masked)}</b> at{" "}
              <b>{displayName(pharmacies.find(p => p.pharmacyId === pharmacyId)?.pharmacyName || "", masked)}</b>,{" "}
              {MONTHS[month - 1]} {year}. Sessions already marked paid are left
              untouched. There is no single undo — clearing them again means
              editing each date by hand.
            </div>
            <div style={{ fontSize: 13, color: "#374151", marginBottom: 14 }}>
              {fmtHours(confirmPay.targets.reduce((a, x) => a + (hoursFor(x) || 0), 0))}
              {" "}on site ·{" "}
              {fmtMoney(confirmPay.targets.reduce((a, x) => a + (payFor(x) || 0), 0))}
              {confirmPay.targets.some(x => payFor(x) == null) &&
                " (some sessions have no rate and are not included in this figure)"}
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button style={S.btnGhost} disabled={paying}
                      onClick={() => setConfirmPay(null)}>Cancel</button>
              <button style={{ ...S.btn, opacity: paying ? 0.6 : 1 }}
                      disabled={paying} onClick={doMarkAll}>
                {paying ? "Saving…" : "Yes, mark them paid"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// TAB 3 — Unpaid sessions
// ===========================================================================
function UnpaidView({ token, onSaved }) {
  const masked = useMaskNames();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState({ key: "date", dir: "desc" });

  const load = useCallback(async () => {
    try {
      const d = await apiGet("rp-signin/unpaid", token);
      setRows(Array.isArray(d?.sessions) ? d.sessions : []);
    } catch (e) {
      if (DEMO_FALLBACK) setRows(demoUnpaid());
      else setError(e.message);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const markPaid = async (s) => {
    setRows(rs => rs.filter(r => r.sessionId !== s.sessionId));
    try {
      await apiSend("rp-signin/payment", token, "POST",
        { pharmacyId: s.pharmacyId, sessionId: s.sessionId, datePaid: isoToday() });
      onSaved && onSaved();
    } catch (e) { if (!DEMO_FALLBACK) { setError(e.message); load(); } }
  };

  const sorted = useMemo(() => {
    const list = [...(rows || [])];
    const get = {
      date:       s => new Date(s.signInTime).getTime(),
      pharmacist: s => (s.pharmacistName || "").toLowerCase(),
      pharmacy:   s => (s.pharmacyName || "").toLowerCase(),
      hours:      s => s.hours ?? hoursBetween(s.signInTime, s.signOutTime) ?? 0
    }[sort.key];
    list.sort((a, b) => {
      const av = get(a), bv = get(b);
      const c = av < bv ? -1 : av > bv ? 1 : 0;
      return sort.dir === "asc" ? c : -c;
    });
    return list;
  }, [rows, sort]);

  const toggle = (key) => setSort(s =>
    s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
                  : { key, dir: "asc" });
  const arrow = (key) => sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : "";

  const totalHours = sorted.reduce((a, s) =>
    a + (s.hours ?? hoursBetween(s.signInTime, s.signOutTime) ?? 0), 0);

  return (
    <div>
      <div style={S.ctrlRow}>
        <span style={{ fontSize: 13, color: "#374151" }}>
          <b>{sorted.length}</b> unpaid session{sorted.length === 1 ? "" : "s"}{" "}
          across the group · {fmtHours(totalHours)} total
        </span>
        <button style={S.btnGhost} onClick={load}>Refresh</button>
      </div>

      {error && <div style={S.error}>{error}</div>}

      {!rows ? <div style={S.sub}>Loading…</div>
       : !sorted.length ? (
        <div style={{ ...S.sub, color: "#059669" }}>
          Everything is marked as paid.
        </div>
      ) : (
        <div style={{ maxHeight: 520, overflow: "auto",
                      border: "1px solid #e5e7eb", borderRadius: 4 }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={{ ...S.th, cursor: "pointer" }}
                    onClick={() => toggle("pharmacist")}>Pharmacist{arrow("pharmacist")}</th>
                <th style={{ ...S.th, cursor: "pointer" }}
                    onClick={() => toggle("pharmacy")}>Pharmacy{arrow("pharmacy")}</th>
                <th style={{ ...S.th, width: 100, cursor: "pointer" }}
                    onClick={() => toggle("date")}>Date{arrow("date")}</th>
                <th style={{ ...S.th, width: 80 }}>Clock in</th>
                <th style={{ ...S.th, width: 80 }}>Clock out</th>
                <th style={{ ...S.th, width: 100, cursor: "pointer" }}
                    onClick={() => toggle("hours")}>Hours on site{arrow("hours")}</th>
                <th style={{ ...S.th, width: 150 }}></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((s, i) => {
                return (
                  <tr key={i}>
                    <td style={S.td}>{displayName(s.pharmacistName || "Unknown", masked)}</td>
                    <td style={S.td}>{displayName(s.pharmacyName, masked)}</td>
                    <td style={S.td}>
                      {fmtSessionDate(s.signInTime)}{" "}
                      <span style={{ fontSize: 11, color: "#6b7280" }}>
                        {sessionDow(s.signInTime)}
                      </span>
                    </td>
                    <td style={S.td}>{fmtTime(s.signInTime)}</td>
                    <td style={S.td}>
                      {s.signOutTime ? fmtTime(s.signOutTime)
                                     : <span style={{ color: "#b45309" }}>open</span>}
                    </td>
                    <td style={S.td}>
                      {fmtHours(s.hours ?? hoursBetween(s.signInTime, s.signOutTime))}
                    </td>
                    <td style={S.td}>
                      <button style={S.btnTiny} onClick={() => markPaid(s)}>
                        Click to mark paid
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Main component
// ===========================================================================
export default function RPSignIn({ token: tokenProp }) {
  const token = tokenProp || localStorage.getItem("token");
  const masked = useMaskNames();

  const now = new Date();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [demo, setDemo] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);          // pharmacyId
  const [tab, setTab] = useState("pharmacy");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  // GPhC -> { name, hourlyRate, ... } from the head-office pharmacist
  // register. Fetched once: it is a small per-organisation list and it does
  // not change while somebody is reconciling a month.
  const [rates, setRates] = useState({});

  // ---- Pharmacist register -------------------------------------------------
  // Same data and the same endpoints the rota module uses, so an edit made
  // here shows up there and vice versa. Loaded lazily: nothing fetches until
  // somebody actually opens the list.
  const [showRegister, setShowRegister] = useState(false);
  const [pharmacists, setPharmacists] = useState([]);
  const [registerError, setRegisterError] = useState(null);

  const rotaGet = useCallback(async (path) => {
    const res = await fetch(`${API_BASE}/api/headoffice/rota${withOrgScope(path)}`,
      { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }, [token]);

  // Mutations are POST across the board: the production IIS/Plesk host blocks
  // PUT and DELETE at the web-server layer (see the rota module's API notes).
  const rotaPost = useCallback(async (path, body) => {
    const res = await fetch(`${API_BASE}/api/headoffice/rota${withOrgScope(path)}`,
      { method: "POST",
        headers: { Authorization: `Bearer ${token}`,
                   "Content-Type": "application/json" },
        body: JSON.stringify(body || {}) });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }, [token]);

  const openRegister = useCallback(async () => {
    setShowRegister(true);
    setRegisterError(null);
    try {
      const d = await rotaGet("/pharmacists");
      setPharmacists(d?.pharmacists || []);
    } catch (e) {
      setRegisterError(`Could not load the pharmacist register (${e.message}).`);
    }
  }, [rotaGet]);

  const createPharmacist = useCallback(async (form) => {
    try {
      const d = await rotaPost("/pharmacists", { pharmacist: form });
      const created = d?.pharmacist;
      if (created) setPharmacists(prev => [...prev, created]);
      return created || null;
    } catch (e) {
      setRegisterError(`Create failed: ${e.message}`);
      return null;
    }
  }, [rotaPost]);

  const updatePharmacist = useCallback(async (id, fields) => {
    const before = pharmacists;
    setPharmacists(prev => prev.map(p => (p.id === id ? { ...p, ...fields } : p)));
    try {
      const d = await rotaPost(`/pharmacists/${encodeURIComponent(id)}/update`,
        { pharmacist: fields });
      if (d?.pharmacist) {
        setPharmacists(prev => prev.map(p => (p.id === id ? d.pharmacist : p)));
      }
      // A rate change moves the money column, so re-read the GPhC->rate map.
      if (fields.hourlyRate !== undefined || fields.gphc !== undefined) {
        try {
          const r = await apiGet("rp-signin/rates", token);
          setRates(r?.byGphc || {});
        } catch (_) { /* stale rates are survivable; a blank column is not an error */ }
      }
      return true;
    } catch (e) {
      setPharmacists(before);           // revert the optimistic edit
      setRegisterError(`Update failed: ${e.message}`);
      return false;
    }
  }, [rotaPost, pharmacists, token]);

  useEffect(() => {
    (async () => {
      try {
        const d = await apiGet("rp-signin/rates", token);
        setRates(d?.byGphc || {});
      } catch (_) {
        setRates({});   // no rates is a blank money column, not an error
      }
    })();
  }, [token]);

  const loadOverview = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await apiGet("rp-signin/today-overview", token);
      setOverview(Array.isArray(d) ? d : (d?.pharmacies || []));
      setDemo(false);
    } catch (e) {
      if (DEMO_FALLBACK) { setOverview(DEMO_OVERVIEW); setDemo(true); }
      else setError(e.message);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (overview || []).filter(r =>
      !q || (r.pharmacyName || "").toLowerCase().includes(q));
  }, [overview, query]);

  const counts = useMemo(() => {
    const all = overview || [];
    return {
      in:   all.filter(r => r.state === "in").length,
      out:  all.filter(r => r.state === "out").length,
      none: all.filter(r => r.state === "none").length
    };
  }, [overview]);

  const tone = (state) =>
    state === "in"  ? { bg: GREEN, bd: GREEN_BD }
  : state === "out" ? { bg: AMBER, bd: AMBER_BD }
  :                   { bg: RED,   bd: RED_BD };

  const selectPharmacy = (id) => {
    setSelected(prev => prev === id ? null : id);
    setTab("pharmacy");
  };

  const selectedRow = (overview || []).find(r => r.pharmacyId === selected);

  return (
    <div style={S.wrap}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={S.h1}>RP sign-in</div>
          <div style={S.sub}>
            Today's responsible-pharmacist cover across the group. Click a pharmacy
            for its session and payment record.
          </div>
        </div>
        <button style={S.btnGhost}
                onClick={openRegister}
                title="Add or edit pharmacists in the head-office register. The hourly pay rate set here is what the money column on the month grid is calculated from, matched on GPhC number.">
          Pharmacist list
        </button>
      </div>

      {demo && (
        <div style={S.banner}>
          Showing sample data — the <code>/api/headoffice/rp-signin/*</code>{" "}
          endpoints aren't live yet. Set <code>DEMO_FALLBACK = false</code> once
          they are.
        </div>
      )}

      <div style={S.legend}>
        <span><i style={S.swatch(GREEN, GREEN_BD)} />
          Signed in now ({counts.in})</span>
        <span><i style={S.swatch(AMBER, AMBER_BD)} />
          Signed in and out ({counts.out})</span>
        <span><i style={S.swatch(RED, RED_BD)} />
          No sign-in today ({counts.none})</span>
        <input style={{ ...S.select, marginLeft: "auto", width: 200 }}
               placeholder="Search pharmacy…"
               value={query} onChange={(e) => setQuery(e.target.value)} />
      </div>

      {error && <div style={S.error}>{error}</div>}
      {loading && <div style={S.sub}>Loading…</div>}

      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>Pharmacy</th>
            <th style={S.th}>Pharmacist</th>
            <th style={{ ...S.th, width: 90 }}>Signed in</th>
            <th style={{ ...S.th, width: 90 }}>Signed out</th>
            <th style={{ ...S.th, width: 110 }}>Hours on site</th>
            <th style={{ ...S.th, width: 160 }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const t = tone(r.state);
            const on = r.pharmacyId === selected;
            return (
              <tr key={r.pharmacyId}
                  onClick={() => selectPharmacy(r.pharmacyId)}
                  style={{ background: t.bg, cursor: "pointer",
                           outline: on ? "2px solid #2563eb" : "none",
                           outlineOffset: -2 }}>
                <td style={{ ...S.td, fontWeight: 600, borderLeft: `4px solid ${t.bd}` }}>
                  {displayName(r.pharmacyName, masked)}
                </td>
                <td style={S.td}>
                  {r.pharmacistName
                    ? displayName(r.pharmacistName, masked)
                    : <span style={S.muted}>—</span>}
                </td>
                <td style={S.td}>{fmtTime(r.signInTime)}</td>
                <td style={S.td}>{fmtTime(r.signOutTime)}</td>
                <td style={S.td}>
                  {fmtHours(r.hours ?? hoursBetween(r.signInTime, r.signOutTime))}
                </td>
                <td style={S.td}>
                  {r.state === "in"  ? "Signed in"
                 : r.state === "out" ? "Signed in and out"
                 :                     <b style={{ color: "#b91c1c" }}>Not signed in</b>}
                </td>
              </tr>
            );
          })}
          {!loading && !rows.length && (
            <tr><td style={{ ...S.td, color: "#6b7280" }} colSpan={6}>
              No pharmacies match.
            </td></tr>
          )}
        </tbody>
      </table>

      {selected && (
        <div style={S.panel}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>
            {displayName(selectedRow?.pharmacyName || "", masked)}
            <button style={{ ...S.btnGhost, float: "right", padding: "3px 9px" }}
                    onClick={() => setSelected(null)}>Close</button>
          </div>

          <div style={S.tabBar}>
            {[["pharmacy", "Pharmacy view"],
              ["unpaid", "Unpaid sessions"]].map(([k, label]) => (
              <div key={k} style={S.tab(tab === k)} onClick={() => setTab(k)}>
                {label}
              </div>
            ))}
          </div>

          {tab === "pharmacy" && (
            <PharmacyView token={token}
                          pharmacies={overview || []}
                          pharmacyId={selected}
                          onPharmacyChange={setSelected}
                          year={year} month={month}
                          rates={rates}
                          onMonthChange={(y, m) => { setYear(y); setMonth(m); }} />
          )}
          {tab === "unpaid" && <UnpaidView token={token} />}
        </div>
      )}

      {showRegister && (
        <>
          {registerError && <div style={S.error}>{registerError}</div>}
          <PharmacistListPopup
            pharmacists={pharmacists}
            createPharmacist={createPharmacist}
            updatePharmacist={updatePharmacist}
            showDays={false}
            onClose={() => { setShowRegister(false); setRegisterError(null); }} />
        </>
      )}
    </div>
  );
}

// ===========================================================================
// Sample data — remove with DEMO_FALLBACK once the endpoints exist
// ===========================================================================
const DEMO_PEOPLE = [
  { pharmacistKey: "p1", name: "Sarah Whitfield", gphc: "2203991" },
  { pharmacistKey: "p2", name: "Imran Choudhury", gphc: "2211043" },
  { pharmacistKey: "p3", name: "Ruth Anderson",   gphc: "2198770" },
  { pharmacistKey: "p4", name: "Daniel Osei",     gphc: "2224518" }
];

const at = (day, h, m) => {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), day, h, m).toISOString();
};

const DEMO_OVERVIEW = [
  { pharmacyId: "a1", pharmacyName: "Southtown Pharmacy",  state: "in",
    pharmacistName: "Sarah Whitfield", signInTime: at(new Date().getDate(), 8, 55),
    signOutTime: null },
  { pharmacyId: "a2", pharmacyName: "Highfield Chemist",   state: "out",
    pharmacistName: "Imran Choudhury", signInTime: at(new Date().getDate(), 9, 0),
    signOutTime: at(new Date().getDate(), 17, 30) },
  { pharmacyId: "a3", pharmacyName: "Market Street Pharmacy", state: "none",
    pharmacistName: null, signInTime: null, signOutTime: null },
  { pharmacyId: "a4", pharmacyName: "Riverside Pharmacy",  state: "in",
    pharmacistName: "Ruth Anderson", signInTime: at(new Date().getDate(), 8, 45),
    signOutTime: null },
  { pharmacyId: "a5", pharmacyName: "Oakwood Pharmacy",    state: "none",
    pharmacistName: null, signInTime: null, signOutTime: null },
  { pharmacyId: "a6", pharmacyName: "Kingsway Pharmacy",   state: "out",
    pharmacistName: "Daniel Osei", signInTime: at(new Date().getDate(), 9, 10),
    signOutTime: at(new Date().getDate(), 13, 0) }
];

function demoMonth(year, month) {
  const out = [];
  const n = daysInMonth(year, month);
  const names = DEMO_PEOPLE.map(p => p.name);
  for (let d = 1; d <= n; d++) {
    const dow = new Date(year, month - 1, d).getDay();
    if (dow === 0) continue;                       // closed Sundays
    const nm = names[(d + month) % names.length];
    const inH = 9, outH = dow === 6 ? 13 : 18;
    out.push({
      sessionId: `s-${year}-${month}-${d}`,
      pharmacistName: nm,
      signInTime: new Date(year, month - 1, d, inH, d % 3 === 0 ? 5 : 0).toISOString(),
      signOutTime: new Date(year, month - 1, d, outH, 0).toISOString(),
      hours: null,
      notes: d % 9 === 0 ? "Covered late — locum agency" : "",
      datePaid: d < 12 ? new Date(year, month - 1, 15).toISOString() : null
    });
  }
  return out;
}

function demoUnpaid() {
  const out = [];
  const n = new Date();
  DEMO_PEOPLE.forEach((p, i) => {
    for (let k = 0; k < 3; k++) {
      const site = DEMO_OVERVIEW[(i + k) % DEMO_OVERVIEW.length];
      const day = 3 + i * 4 + k * 2;
      out.push({
        sessionId: `u-${i}-${k}`, pharmacyId: site.pharmacyId,
        pharmacyName: site.pharmacyName, pharmacistName: p.name,
        signInTime: new Date(n.getFullYear(), n.getMonth(), day, 9, 0).toISOString(),
        signOutTime: new Date(n.getFullYear(), n.getMonth(), day, 18, 0).toISOString(),
        hours: null
      });
    }
  });
  return out;
}

// ===========================================================================
// BACKEND CONTRACT (headoffice.js)
// ===========================================================================
//
// New per-tenant collection: rp_signin_payments
//   { _id, sessionId (ObjectId → rp_signin_logs._id),
//     datePaid: Date|null, notes: String,
//     updatedBy, updatedAt, createdAt, isDeleted: false }
//   mongoIndexes.js:
//     { collection: 'rp_signin_payments', spec: { sessionId: 1 }, unique: true }
//     { collection: 'rp_signin_payments', spec: { isDeleted: 1, datePaid: 1 } }
//
// GET  rp-signin/today-overview
//        → [{ pharmacyId, pharmacyName, dbName, state: 'in'|'out'|'none',
//             pharmacistName, signInTime, signOutTime, hours }]
//        state: open session today → 'in'; closed session today → 'out';
//               nothing today → 'none'. (Latest session wins if several.)
//
// GET  rp-signin/month?pharmacyId=&year=&month=
//        → { sessions: [{ sessionId, pharmacistName, signInTime, signOutTime,
//                         hours, notes, datePaid }] }
//        Left-joins rp_signin_payments on sessionId.
//
// GET  rp-signin/rates
//        → { byGphc: { '<gphc>': { name, type, hourlyRate, active } } }
//        Read from pharmace_common_db.rota_pharmacists for the token's
//        organisation. GPhC is the join key between an RP session (tenant
//        staffs) and the pay register (head office).
//
// POST rp-signin/payment/bulk
//        body { pharmacyId, sessionIds: [...], datePaid? }
//        Every id is verified against THIS tenant before anything is
//        written; ids from elsewhere come back in `notFound`. datePaid
//        defaults to today (UK). Idempotent.
//
// GET  rp-signin/unpaid
//        → { sessions: [{ sessionId, pharmacyId, pharmacyName, pharmacistName,
//                         signInTime, signOutTime, hours }] }
//        Fan-out across listFleetTenants; respects FLEET_SCAN_LIMIT.
//
// POST rp-signin/payment
//        body { pharmacyId, sessionId, datePaid?, notes? }
//        upsert on { sessionId }, $set only the supplied fields.
//        datePaid: null clears it.